import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Box, Button, CssBaseline, Typography } from '@mui/material';
import GallerySidePanel from '../Gallery/GallerySidePanel';
import HeaderInfo from './HeaderInfo';
import BadgeCount from './BadgeCount';
import IconViewGallery from '../../assets/icons/IconViewGallery';
import IconEmptyFlag from '../../assets/icons/IconEmptyFlag';
import getScriptHash from './hooks/getScriptHash';
import useGetLuggage from './hooks/getLuggage';
import { decodeUnzip } from '../Decryption/Decryption';
import { useDispatch, useSelector } from 'react-redux';
import Notifications from '../../ui/molecules/toast/Notifications';
import {
  getScriptSave,
  getScriptState,
  getAdditionalPhotoLabels,
  getScriptData as getCorescriptData,
  loadScript,
  LoadScriptProps,
  setAccept,
  resetCoreScript,
} from '../../redux/reducer/coreScriptReducer';
import {
  persistor,
  RootState,
  useAppSelector,
  AppDispatch,
} from '../../redux/Store';
import {
  ActivatedByMapper,
  SaveDataToResults,
} from '@safeguardproperties/corescript-react-npm';

import StationsList from './stations';
import useDecision from './hooks/UseDecision';
import {
  filterData,
  useVisulizationCountInStation,
} from '../../utils/helpers/formatValue';
import {
  SaveData,
  ScriptData,
  ScriptResultsAccess,
  ScriptResultsAnswer,
  ScriptResultsWorkOrder,
  ScriptResultsAnswerMediaGroup,
  ScriptResultsAnswerMedia,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import useSaveData from './hooks/useSaveData';
import CustomTypography from '../../ui/atoms/CustomTypography';
import {
  resetContextId,
  setContextId,
  setIsGalleryPanelOpen,
  setIsCountPanelOpen,
  setQueryObj,
  setViewFlagged,
  setIsReadOnly,
  setLoadedFirstTime,
} from '../../redux/reducer/contextIdReducer';
import {
  useSinglePostMessage,
  cancelPostMessage,
} from '../../utils/helpers/usePostMessage';
import useImages from './hooks/useImages';
import {
  resetState,
  toggleAllQuestions,
} from '../../redux/reducer/selectedQuestionReducer';
import { PersistHelper } from '../../redux/persistHelper';
import SkeletonLoader from '../../ui/molecules/skeleton/SkeletonLoader';
import { Platform } from '../../constants/platform';
import { useLocation } from 'react-router';
import { decryptJSON } from '../../utils/helpers/Encryption';
import {
  setLuggageData,
  setOrder,
} from '../../redux/reducer/inspact/order_reducer';
import { contextValue } from '../../constants';
import useQueryObject from '../../hooks/useQueryObject';
import FinsihedDialog from '../../ui/atoms/FinishedDialog';
import ForbiddenDialog from '../../ui/atoms/ForbiddenDialog';
import { setShow403Dialog } from '../../redux/reducer/forbiddenDetectorReducer';
import { setPageLoadingMode } from '../../redux/reducer/loadingReducer';
import SidePanel from './SidePanel';
import { CancelButton, SubmitButton } from './ActionButtons';

const closeWindow = (context: string, key: string, id: string) => {
  cancelPostMessage(context, key, id); // Send cancel message before closing
  window.close();
};
function MainComponent() {
  const dispatch = useDispatch<AppDispatch>();
  const scrollToStationRef = useRef<((index: number) => void) | null>(null);

  const show403Dialog = useSelector(
    (state: RootState) => state.forbiddenDetector.show403Dialog
  );
  const loadedfirstTime = useSelector(
    (state: RootState) => state.contextId.loadedfirstTime
  );
  const timestamp = useAppSelector((state) => state.coreScript.scriptTimestamp);
  // const { state } = useAppSelector((state) => state.coreScript.coreScript);
  const stateData = useSelector(getScriptState);
  const saveData = useSelector(getScriptSave);
  const additionalPhotoLabels = useSelector(getAdditionalPhotoLabels);

  const scriptData = useSelector(getCorescriptData);
  const isAudit = useSelector((state: RootState) => state.contextId.isAudit);

  const currentOrder = useAppSelector(
    (state) => state.inspactOrder.currentOrder
  );
  const { contextId, queryObj } = useSelector(
    (state: RootState) => state.contextId
  );
  const sidePanelOpen = useSelector(
    (state: RootState) => state.contextId.isGalleryPanelOpen
  );
  const countSidePanelOpen = useSelector(
    (state: RootState) => state.contextId.isCountPanelOpen
  );

  const showSubmitDataError = useSelector(
    (state: RootState) => state.contextId.showSubmitDataError
  );
  const viewFlagged = useSelector(
    (state: RootState) => state.contextId.viewFlagged
  );
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );
  const checkboxState = useSelector(
    (state: RootState) => state.selectedQuestion
  );

  const {
    isAuditResultsBySubmissionId,
    isContextSupportGallery,
    isContextSupportSidePanel,
    isContextSidePanelOpenByDefault,
    readOnlyAfterSubmit,
    saveWorkInfoWithSubmit,
    allowAddtionalImageLabels,
    orderNumber,
    propertyLoanId,
    submissionId,
  } = useQueryObject();

  const [isExpanded, setIsExpanded] = useState(false);

  // const [viewFlagged, setViewFlagged] = useState(false); // New state for flagged view

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [labelImages, setLabelImages] = useState<(File | null)[][]>(
    new Array(12).fill([]).map(() => [])
  );

  const { keyValues } = useAppSelector((state) => state.language);
  const {
    getScriptData,
    isGetScriptPending,
    isGetScriptLanguagePackPending,
    isErrorScript,
  } = getScriptHash();

  const { luggageData, isLuggagehDataFetching, isLuggageError } =
    useGetLuggage();

  const {
    postSubmitData,
    isSubmitDataPending,
    successfulSubmitData,
    submitDataError,
  } = useSaveData();

  const {
    SeededData,
    isSeededFetching,
    isErrorCoreScriptInitData,
    auditFailures,
    isAuditFailuresFetching,
    currentAnswers,
    isCurrentAnswersFetching,
    priorDecisions,
    isPriorDecisionsFetching,
    decisionReasons,
    isDecisionReasonsFetching,
  } = useDecision();

  const { fetchImages } = useImages();

  if (
    orderNumber &&
    propertyLoanId &&
    submissionId &&
    currentOrder?.orderNumber !== orderNumber &&
    currentOrder?.propertyLoanId !== propertyLoanId
  ) {
    dispatch(
      setOrder({ orderNumber: orderNumber, propertyLoanId: propertyLoanId })
    );
    dispatch(setContextId(submissionId));
    dispatch(
      setQueryObj({
        context: contextValue?.AuditResultsBySubmissionId,
        key: 'Submission.ID',
        id: submissionId,
      })
    );
  }

  const stations = useMemo(() => {
    if (isAudit) {
      const filtered = filterData(stateData?.Stations, viewFlagged, saveData);

      return filtered;
    }

    return stateData?.Stations;
  }, [saveData, stateData?.Stations, viewFlagged, isAudit]);

  const count = useVisulizationCountInStation(stations);

  useEffect(() => {
    String.prototype.toLanguagePack = function (): string {
      const key = String(this);
      if (keyValues && keyValues[key]) {
        return keyValues[key];
      }

      // HOTFIX: This is just a hot fix until we figure out how we want to handle this label ( as it is not part of the script )
      if (key === 'WorkOrder.FTVForm.Photo') {
        return 'FTV Form';
      }

      return key;
    };
  }, [keyValues]);

  useEffect(() => {
    if (isContextSidePanelOpenByDefault) {
      dispatch(setIsCountPanelOpen(true)); // Open the side panel by default
      dispatch(setIsGalleryPanelOpen(false)); // Ensure the gallery panel is closed
    }
  }, [isContextSidePanelOpenByDefault, dispatch]);

  useSinglePostMessage(stateData);

  const handleCloseSidePanel = () => {
    dispatch(setIsGalleryPanelOpen(false));
    dispatch(setIsCountPanelOpen(false));
    setIsExpanded(false);
  };

  const handleExpandPanel = (expand: boolean) => {
    setIsExpanded(expand);
  };

  const handleToggleFlaggedView = () => {
    dispatch(setViewFlagged(!viewFlagged));
  };

  const handleSubmit = () => {
    let wr = {} as ScriptResultsWorkOrder;
    if (saveWorkInfoWithSubmit) {
      wr = {
        Script: saveData.ScriptHash,
        ScriptVersion: saveData.ScriptVersion,
        OrderNumber: orderNumber,
      } as ScriptResultsWorkOrder;
    }

    const sr = SaveDataToResults(
      scriptData as ScriptData,
      saveData as SaveData,
      {} as ScriptResultsAccess,
      wr
    );

    if (allowAddtionalImageLabels) {
      const groups = {
        Name: 'Safeguard.Media.UpdateDescriptions',
        Values: ['NA'] as string[],
      } as ScriptResultsAnswer;
      groups.MediaGroup = [] as ScriptResultsAnswerMediaGroup[];
      for (const label of additionalPhotoLabels) {
        if (label.Images && label.Images.length > 0) {
          const Guids = { GUID: [] } as ScriptResultsAnswerMedia;
          const labeltrans = label.Label.toLanguagePack();
          const v = {
            Type: 'Photo',
            Label: labeltrans,
            Media: [],
          } as ScriptResultsAnswerMediaGroup;
          for (const image of label.Images) {
            Guids.GUID.push(image.guid);
          }
          v.Media.push(Guids);
          groups.MediaGroup.push(v);
        }
      }
      sr.Result.Answers.push(groups);
    }

    // Remove answers with "Select Option"
    sr.Result.Answers = sr.Result.Answers.filter(
      (answer) => answer.Values[0] !== 'Select Option'
    );

    postSubmitData({
      parameter: sr,
      AuditSaved: stateData?.AuditSaved,
      Followup: stateData?.Followup,
    })
      .then(async (result) => {
        await persistor.purge();
        if (readOnlyAfterSubmit) {
          dispatch(setIsReadOnly(true));
        }
        dispatch(resetContextId());
      })
      .catch((error) => {
        Notifications(error?.message, 0);
      });
  };

  const handleToggleAllQuestions = () => {
    const isChecked = !checkboxState.allSelected; // Toggle based on current state
    const stationsMapped = stations?.map((station: any) => ({
      title: station.title,
      questionKeys: station?.data
        ?.filter(
          (q: any) =>
            !q?.AuditDecision?.DecisionId &&
            q?.Medallions !== undefined &&
            saveData?.Answers[q?.Title]?.Answers?.length > 0 &&
            saveData?.Answers[q?.Title]?.Answers.some(
              (answer: any) => answer?.Answer !== ''
            )
        )
        ?.map((q: any) => q.QuestionKey),
    }));

    dispatch(toggleAllQuestions({ stations: stationsMapped, isChecked })); // Use the renamed variable
  };

  const checkForDisabling = () => {
    if (isReadOnly) {
      return true; // Disable if `isReadOnly` is true
    }

    return stations?.every((station: any) =>
      station?.data?.every(
        (q: any) => q?.AuditDecision?.DecisionId || q?.Medallions === undefined
      )
    );
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getAllQuestions = (stations: { [key: string]: string[] }) => {
    // Use Object.values to extract all arrays and flatten them
    return Object.values(stations).flat();
  };

  const handleAcceptAll = () => {
    dispatch(
      setAccept({
        questionPaths: getAllQuestions(checkboxState?.stations),
      })
    );
    dispatch(resetState());
  };

  const loadData = useCallback(() => {
    if (
      SeededData &&
      luggageData &&
      getScriptData &&
      getScriptData.scriptValueGzip &&
      getScriptData.labelsGzip &&
      ((isAudit && decisionReasons) || isAudit === false) &&
      ((isAudit && priorDecisions) || isAudit === false) &&
      ((isAudit && currentAnswers) || isAudit === false) &&
      loadedfirstTime === false
    ) {
      try {
        const decoded = decodeUnzip(getScriptData?.scriptValueGzip);
        const decodedLabels = decodeUnzip(getScriptData?.labelsGzip);

        const mapping = ActivatedByMapper(JSON.stringify(decoded));
        const paersedMapping = JSON.parse(mapping);
        const payload = {
          scriptHash: getScriptData?.scriptHash,
          scriptData: mapping,
          luggageData: luggageData,
          seededData: JSON.stringify(SeededData),
          medallionData: isAudit ? JSON.stringify(auditFailures) : undefined,
          auditCurrentData: isAudit
            ? JSON.stringify(currentAnswers)
            : undefined,
          auditDecisionsData: isAudit
            ? JSON.stringify(priorDecisions)
            : undefined,
          decisionReasonsData: isAudit
            ? JSON.stringify(decisionReasons)
            : undefined,
          additionalLabelList: JSON.stringify(decodedLabels),
        } as LoadScriptProps;

        const parsedLuggageData = JSON.parse(luggageData);
        const luggageStateData =
          parsedLuggageData?.Luggages ?? parsedLuggageData;

        dispatch(setLuggageData(luggageStateData));
        dispatch(loadScript(payload));
        dispatch(setIsGalleryPanelOpen(!isAudit));
        dispatch(setLoadedFirstTime(true));
      } catch (error) {
        console.log('exception', error);
      }
    }
  }, [
    luggageData,
    getScriptData,
    SeededData,
    isAudit,
    auditFailures,
    currentAnswers,
    priorDecisions,
    decisionReasons,
    loadedfirstTime,
    dispatch,
  ]);

  // Check for errors based on the conditions
  const hasError = useMemo(() => {
    return (
      isErrorScript ||
      isErrorCoreScriptInitData ||
      isLuggageError ||
      showSubmitDataError
    );
  }, [
    showSubmitDataError,
    isErrorScript,
    isLuggageError,
    isErrorCoreScriptInitData,
  ]);

  const showSpiner = useMemo(() => {
    return (
      isAuditFailuresFetching ||
      isCurrentAnswersFetching ||
      isGetScriptPending ||
      isGetScriptLanguagePackPending ||
      isLuggagehDataFetching ||
      isSeededFetching ||
      isPriorDecisionsFetching ||
      isSubmitDataPending
    );
  }, [
    isAuditFailuresFetching,
    isCurrentAnswersFetching,
    isGetScriptPending,
    isGetScriptLanguagePackPending,
    isLuggagehDataFetching,
    isSeededFetching,
    isPriorDecisionsFetching,
    isSubmitDataPending,
  ]);
  const isskeletonLoader =
    Platform?.isAudit() && globalThis.self !== globalThis.top;

  // Drive the single shared GlobalSpinner from this page's loading state instead
  // of rendering a bespoke loader here:
  //   'spinner'  -> normal full-page load, show the shared spinner
  //   'skeleton' -> we render the audit skeleton, so suppress the shared spinner
  //   'none'     -> not loading
  // This MUST be useLayoutEffect, not useEffect: it runs before paint, so the
  // shared spinner is in the right state on the same frame this component decides
  // to load. With useEffect the flag lags one commit and spinners can flash. Reset
  // to 'none' on unmount.
  useLayoutEffect(() => {
    dispatch(
      setPageLoadingMode(
        showSpiner ? (isskeletonLoader ? 'skeleton' : 'spinner') : 'none'
      )
    );
    return () => {
      dispatch(setPageLoadingMode('none'));
    };
  }, [showSpiner, isskeletonLoader, dispatch]);

  const { uploadQueue, cloneQueue } = useAppSelector((state) => state.gallery);

  const shouldFetchImages = useMemo(() => {
    const isUploadQueueCompleted =
      uploadQueue?.length > 0
        ? uploadQueue.every(
            (item) =>
              item.status === 'success' ||
              item.status === 'failed' ||
              item.status === 'rejected'
          )
        : true;
    const isCloneQueueEmpty = cloneQueue.length === 0;
    return isUploadQueueCompleted && isCloneQueueEmpty;
  }, [uploadQueue, cloneQueue]);

  useEffect(() => {
    if (show403Dialog) {
      return;
    }
    if (contextId && shouldFetchImages) {
      fetchImages().catch((error) => {
        Notifications(error?.message, 0);
      });
    }
  }, [fetchImages, shouldFetchImages, show403Dialog, contextId]);

  useEffect(() => {
    if (show403Dialog) {
      return;
    }
    if (isErrorScript || isErrorCoreScriptInitData || isLuggageError) {
      dispatch(resetState());
      dispatch(resetCoreScript());
      PersistHelper.clearAllData();
      return;
    }

    loadData();
  }, [
    luggageData,
    getScriptData,
    isErrorScript,
    isErrorCoreScriptInitData,
    isLuggageError,
    show403Dialog,
    loadData,
    dispatch,
  ]);

  const shouldSubmitBeDisabled = () => {
    return (
      isSubmitDataPending ||
      isReadOnly ||
      !(
        stateData?.Followup ||
        (stateData?.ScriptComplete && stateData?.MediaComplete)
      )
    );
  };

  if (showSpiner) {
    // No bespoke spinner here. The audit skeleton is a distinct loading modality
    // for the embedded case, so keep it; otherwise render nothing and let the
    // shared GlobalSpinner (driven by the flags above) cover the page.
    return isskeletonLoader ? (
      <SkeletonLoader width='100%' height={100} variant='text' count={5} />
    ) : null;
  } else if (
    successfulSubmitData &&
    queryObj?.context === contextValue?.INSPI
  ) {
    return (
      <FinsihedDialog>
        Order completed. Click finish to close window
      </FinsihedDialog>
    );
  } else if (hasError && !show403Dialog) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Typography variant='h6'>
          {submitDataError || showSubmitDataError
            ? `Unexpected error has occurred: ${submitDataError?.message ?? ''}`
            : 'Order not ready, please try again in 15 minutes'}
        </Typography>
      </Box>
    );
  }

  // if (!isErrorScript && isLuggageError)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1, // Takes all available space dynamically
        padding: 2,
        backgroundColor: '#BFBFBF0D',
        height: '100vh', // Take full height of the viewport
        overflow: 'hidden', // Prevents unnecessary scrollbars
      }}
    >
      {timestamp && (
        <CustomTypography
          variant='body1'
          sx={{
            color: 'black',
            fontFamily: 'Montserrat',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            textTransform: 'capitalize',
            marginX: '0.5rem',
          }}
        >
          Script Date - {timestamp}
          <br />
          {orderNumber ? `Order #: ${orderNumber}` : null}
        </CustomTypography>
      )}
      <ForbiddenDialog>
        You’ve been inactive for a while or your session has timed out. Please
        log back in to continue where you left off.
      </ForbiddenDialog>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Left section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginY: '20px',
            // hides: show403Dialog ? 'none' : 'flex',
          }}
        >
          <HeaderInfo luggageData={luggageData}></HeaderInfo>
          <BadgeCount count={count}></BadgeCount>
        </Box>

        {/* Right section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {isAudit ? (
            <Button
              variant='text'
              sx={{
                display: 'flex',
                padding: '10px 0px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
                alignSelf: 'stretch',
              }}
              onClick={handleToggleFlaggedView} // Toggle flagged view
            >
              <IconEmptyFlag />
              <Typography
                variant='body1'
                sx={{
                  color: '#343330',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px' /* 200% */,
                }}
              >
                {viewFlagged ? 'View All' : 'View Flagged'}
              </Typography>
            </Button>
          ) : (
            !isAuditResultsBySubmissionId && (
              <CancelButton
                onClick={() =>
                  closeWindow(
                    queryObj?.context || '',
                    queryObj?.key || '',
                    queryObj?.id || ''
                  )
                }
                label={
                  saveWorkInfoWithSubmit && isReadOnly ? 'Finished' : 'Cancel'
                }
              />
            )
          )}
          {isAudit && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                sx={{
                  color: '#343330',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px',
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'all !important', // Override pointer-events: none
                  },
                }}
                onClick={handleToggleAllQuestions}
                disabled={checkForDisabling()}
              >
                {checkboxState.allSelected ? 'Uncheck All' : 'Check All'}
              </Button>
              <Button
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px',
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'all !important', // Override pointer-events: none
                  },
                }}
                disabled={!checkboxState?.allSelected}
                onClick={handleAcceptAll}
              >
                Accept All
              </Button>
            </Box>
          )}
          {!isAuditResultsBySubmissionId && (
            <SubmitButton
              disabled={shouldSubmitBeDisabled()}
              onClick={handleSubmit}
            />
          )}
        </Box>
      </Box>

      {(isContextSupportGallery || isContextSupportSidePanel) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='text'
            sx={{
              padding: '10px 12px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              alignSelf: 'stretch',
              visibility: isContextSupportGallery ? 'visible' : 'hidden',
            }}
            onClick={() => {
              dispatch(setIsCountPanelOpen(false));
              dispatch(setIsGalleryPanelOpen(!sidePanelOpen));
            }}
          >
            <IconViewGallery />
            <Typography
              variant='body1'
              sx={{
                color: '#1C6EC9',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '28px' /* 200% */,
              }}
            >
              {sidePanelOpen ? 'Hide Galley' : 'View Gallery'}
            </Typography>
          </Button>
          <Button
            variant='text'
            sx={{
              padding: '10px 12px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              alignSelf: 'stretch',
              visibility: isContextSupportSidePanel ? 'visible' : 'hidden',
            }}
            onClick={() => {
              dispatch(setIsCountPanelOpen(!countSidePanelOpen));
              dispatch(setIsGalleryPanelOpen(false));
            }}
          >
            <IconViewGallery />
            <Typography
              variant='body1'
              sx={{
                color: '#1C6EC9',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '28px' /* 200% */,
              }}
            >
              {countSidePanelOpen ? 'Hide Side Panel' : 'View Side Panel'}
            </Typography>
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: '10px',
          overflow: 'hidden', // Prevent extra scrolling
        }}
      >
        <CssBaseline />

        {!isExpanded && (
          <Box
            sx={{
              flex: 1,
              // flexGrow: sidePanelOpen ? 0.99 : 1, // Adjust width dynamically
              // width: sidePanelOpen ? 'calc(100vh - 376px)' : '100%', // Reduce width when side panel is open
              overflowY: 'auto', // Enables vertical scrolling
              maxHeight: Platform?.isAudit()
                ? 'calc(100vh - 5rem)'
                : 'calc(100vh - 15rem)', // Reserve space for header (~11rem) plus the bottom Cancel/Submit action row (~4rem) so neither is clipped
            }}
          >
            <StationsList
              stations={stations}
              onScrollToStation={(scrollToStation) => {
                scrollToStationRef.current = scrollToStation; // Store the function
              }}
            />
          </Box>
        )}
        {isContextSupportGallery && sidePanelOpen && (
          <GallerySidePanel
            open={sidePanelOpen}
            onClose={handleCloseSidePanel}
            onExpand={handleExpandPanel}
            expanded={isExpanded}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            labelImages={labelImages}
            setLabelImages={setLabelImages}
            allowAddtionalImageLabels={allowAddtionalImageLabels}
          />
        )}
        {isContextSupportSidePanel && countSidePanelOpen && (
          <SidePanel
            onClose={handleCloseSidePanel}
            onNavigateToStation={(index) => {
              if (scrollToStationRef.current) {
                scrollToStationRef.current(index); // Call the scrollToStation function
              }
            }}
          />
        )}
      </Box>

      {/* Bottom action buttons - mirrors the header Cancel/Submit */}
      {!isAudit && !isAuditResultsBySubmissionId && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
            marginTop: '10px',
          }}
        >
          <CancelButton
            onClick={() =>
              closeWindow(
                queryObj?.context || '',
                queryObj?.key || '',
                queryObj?.id || ''
              )
            }
            label={saveWorkInfoWithSubmit && isReadOnly ? 'Finished' : 'Cancel'}
          />
          <SubmitButton
            disabled={shouldSubmitBeDisabled()}
            onClick={handleSubmit}
          />
        </Box>
      )}
    </Box>
  );

  // return (
  //   <Box
  //     sx={{
  //       display: 'flex',
  //       flexDirection: 'column',
  //       alignItems: 'center',
  //       justifyContent: 'center',
  //       height: '100vh',
  //     }}
  //   >
  //     <Typography variant='h6'>Unable to load the component.</Typography>
  //   </Box>
  // );
}

export default MainComponent;
